import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import ProductImagePlaceholder from './ProductImagePlaceholder';

const ProductCard = ({ product, onAddToCart, index }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedStorage, setSelectedStorage] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [justAdded, setJustAdded] = useState(false);

  // Initialize selection with first variant
  useEffect(() => {
    if (product.variants && product.variants.length > 0) {
      const firstVariant = product.variants[0];
      setSelectedStorage(firstVariant.storage);
      setSelectedColor(firstVariant.color);
      setSelectedVariant(firstVariant);
    }
  }, [product]);

  // Get unique storage options
  const getStorageOptions = () => {
    if (!product.variants || product.variants.length === 0) return [];
    return [...new Set(product.variants.map(v => v.storage))];
  };

  // Get color options for selected storage
  const getColorOptions = () => {
    if (!product.variants || !selectedStorage) return [];
    return product.variants.filter(v => v.storage === selectedStorage);
  };

  const handleStorageChange = (storage) => {
    setSelectedStorage(storage);
    const firstVariantWithStorage = product.variants.find(v => v.storage === storage);
    if (firstVariantWithStorage) {
      setSelectedColor(firstVariantWithStorage.color);
      setSelectedVariant(firstVariantWithStorage);
    }
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    const variant = product.variants.find(
      v => v.storage === selectedStorage && v.color === color
    );
    if (variant) {
      setSelectedVariant(variant);
    }
  };

  const getDisplayPrice = () => {
    if (selectedVariant && selectedVariant.price) {
      return selectedVariant.price;
    }
    if (product.salePrice) {
      return product.salePrice;
    }
    return product.basePrice || 0;
  };

  const handleAddToCart = () => {
    const itemToAdd = {
      ...product,
      selectedStorage: selectedVariant?.storage || null,
      selectedColor: selectedVariant?.color || null,
      selectedHex: selectedVariant?.hex || null,
      finalPrice: getDisplayPrice(),
      cartId: `${product.id}-${selectedVariant?.storage || 'default'}-${selectedVariant?.color || 'default'}`
    };

    onAddToCart(itemToAdd);

    // Micro feedback on the button
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1200);
  };

  const storageOptions = getStorageOptions();
  const colorOptions = getColorOptions();
  const currentPrice = getDisplayPrice();

  return (
    <div className="h-full w-[300px] md:w-[340px] flex-shrink-0 snap-center">
      <div className="group relative bg-white rounded-[1.75rem] p-6 md:p-7 h-full flex flex-col text-center border border-gray-100/80 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:shadow-[0_24px_48px_-16px_rgba(0,0,0,0.16)] hover:-translate-y-1.5">
        {/* Tag badge */}
        {product.tag && (
          <span className="absolute top-5 left-5 z-10 text-[10px] font-bold tracking-[0.15em] uppercase text-orange-600 bg-orange-50 border border-orange-200/60 px-3 py-1.5 rounded-full">
            {product.tag}
          </span>
        )}

        {/* Image */}
        <div className="relative w-full aspect-[4/5] mb-6 flex items-center justify-center bg-gradient-to-b from-[#fbfbfd] to-[#f5f5f7] rounded-2xl overflow-hidden">
          <div className="transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06] w-full h-full flex items-center justify-center p-4">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                loading="lazy"
                className="max-w-full max-h-full object-contain drop-shadow-[0_12px_24px_rgba(0,0,0,0.08)]"
              />
            ) : (
              <ProductImagePlaceholder
                type={product.subcategoryId || product.categoryId}
                color={product.colorClass}
              />
            )}
          </div>
          {/* Soft sheen on hover */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/0 to-white/40 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
        </div>

        {/* Info */}
        <div className="flex-1 flex flex-col items-center w-full">
          <h3 className="text-[21px] font-semibold text-[#1d1d1f] tracking-tight leading-snug">
            {product.name}
          </h3>
          {product.specs && (
            <p className="text-[14px] text-gray-500 font-medium mt-1.5 line-clamp-2">
              {product.specs}
            </p>
          )}

          {/* Storage selector */}
          {storageOptions.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 mt-4">
              {storageOptions.map((storage) => (
                <button
                  key={storage}
                  onClick={() => handleStorageChange(storage)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 active:scale-95 ${
                    selectedStorage === storage
                      ? 'bg-[#1d1d1f] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {storage}
                </button>
              ))}
            </div>
          )}

          {/* Color selector */}
          {colorOptions.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2.5 mt-3.5">
              {colorOptions.map((variant) => (
                <button
                  key={variant.color}
                  onClick={() => handleColorChange(variant.color)}
                  className={`relative w-7 h-7 rounded-full transition-all duration-300 ${
                    selectedColor === variant.color
                      ? 'ring-2 ring-[#1d1d1f] ring-offset-2 scale-110'
                      : 'hover:scale-110 opacity-80 hover:opacity-100'
                  }`}
                  title={variant.color}
                  aria-label={`Color: ${variant.color}`}
                >
                  <span
                    className="block w-full h-full rounded-full border border-black/10 shadow-inner"
                    style={{ backgroundColor: variant.hex }}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Price + CTA */}
          <div className="mt-auto pt-6 w-full flex items-center justify-between gap-3">
            <div className="text-left">
              <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Price</p>
              <span className="text-[19px] font-bold text-orange-500 tabular-nums">
                ${currentPrice.toLocaleString()}
              </span>
            </div>

            <button
              onClick={handleAddToCart}
              className={`h-11 px-6 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-all duration-300 active:scale-95 shadow-[0_8px_20px_-6px_rgba(0,0,0,0.35)] ${
                justAdded
                  ? 'bg-orange-500 text-white'
                  : 'bg-[#1d1d1f] text-white hover:bg-black hover:shadow-[0_12px_28px_-6px_rgba(0,0,0,0.45)]'
              }`}
            >
              {justAdded ? (
                'Added ✓'
              ) : (
                <>
                  <Plus className="w-4 h-4" strokeWidth={2.5} />
                  Buy
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
